import React from 'react';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Box, Button, Chip, Stack, Tooltip, Typography } from '@mui/material';

import { CatalogRoom, RoomMatchResult } from '~/types/stopsell';

import MatchActions from './MatchActions';

interface Props {
  match: RoomMatchResult;
  index: number;
  catalogRooms: Array<CatalogRoom>;
  isConfirmed: boolean;
  resolvedCatalogRoomId: string | null;
  onConfirm: (index: number) => void;
  onChange: (index: number) => void;
  onOverride: (index: number, catalogRoomId: string) => void;
}

export default function RoomMatchRow({
  match,
  index,
  catalogRooms,
  isConfirmed,
  resolvedCatalogRoomId,
  onConfirm,
  onChange,
  onOverride,
}: Props) {
  const borderColor = isConfirmed || match.isExact ? 'success.main' : 'warning.main';
  const resolvedRoom = catalogRooms.find((r) => r.id === resolvedCatalogRoomId);

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderLeft: '4px solid',
        borderLeftColor: borderColor,
        borderRadius: 1,
        p: 1.5,
      }}
    >
      <Stack direction="row" alignItems="center" gap={1} justifyContent="space-between">
        <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: 0, flex: 1 }}>
          {match.isExact ? (
            <CheckCircleOutlineIcon fontSize="small" color="success" sx={{ flexShrink: 0, mt: 0.25 }} />
          ) : (
            <Tooltip title="AI extracted room name" placement="top">
              <HelpOutlineIcon fontSize="small" color="action" sx={{ flexShrink: 0, mt: 0.25, cursor: 'help' }} />
            </Tooltip>
          )}
          <Typography variant="body2" fontWeight={500} noWrap>
            {match.extractedName}
          </Typography>
        </Stack>

        <Chip
          label={isConfirmed || match.isExact ? 'Matched' : 'Review'}
          size="small"
          color={isConfirmed || match.isExact ? 'success' : 'warning'}
          variant="outlined"
          sx={{ flexShrink: 0 }}
        />
      </Stack>

      {match.isExact && (
        <Stack direction="row" alignItems="center" gap={0.5} sx={{ mt: 0.75, pl: 3.5 }}>
          <Typography variant="body2" color="text.secondary">
            Matches:
          </Typography>
          <Typography variant="body2" fontWeight={500}>
            {match.catalogRoomName}
          </Typography>
        </Stack>
      )}

      {!match.isExact && !isConfirmed && (
        <MatchActions
          match={match}
          index={index}
          catalogRooms={catalogRooms}
          onConfirm={onConfirm}
          onOverride={onOverride}
        />
      )}

      {!match.isExact && isConfirmed && (
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1, pl: 3.5 }}>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <CheckCircleOutlineIcon fontSize="small" color="success" />
            <Typography variant="body2" color="success.dark">
              Mapped to <strong>{resolvedRoom?.name ?? resolvedCatalogRoomId}</strong>
            </Typography>
          </Stack>
          <Button size="small" variant="text" onClick={() => onChange(index)}>
            Change
          </Button>
        </Stack>
      )}
    </Box>
  );
}
